import React from "react";
import Dialog from "@mui/material/Dialog";
import Avatar from "@mui/material/Avatar";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AppButton from "./AppButton";

const CommentTextareaModal = ({
  open,
  title,
  onClose,
  onSubmit,
  value,
  onChange,
  loading = false,
  submitLabel = "Post Your Topic",
  loadingLabel = "Posting...",
  cancelLabel = "Cancel",
  authorName = "",
  authorEmail = "",
  children,
}) => {
  const initials = authorName
    ? authorName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "U";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* ── gradient header ── */}
      <div
        style={{
          background: "var(--app-ai-assistant-gradient, linear-gradient(135deg, #253978 0%, #1a2a5e 60%, #0f1e45 100%))",
          padding: "20px 24px 16px",
          position: "relative",
        }}
      >
        {/* decorative circles */}
        <div
          style={{
            position: "absolute", top: -20, right: -20,
            width: 80, height: 80, borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          }}
        />
        <div
          style={{
            position: "absolute", bottom: -10, right: 40,
            width: 40, height: 40, borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
          }}
        />

        <div className="flex items-center justify-between relative">
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ background: "rgba(255,255,255,0.12)" }}
            >
              <EditNoteIcon sx={{ fontSize: 18, color: "white" }} />
            </div>
            <span className="text-white font-bold text-base tracking-wide">{title}</span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full transition-all"
            style={{ background: "rgba(255,255,255,0.1)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
          >
            <CloseIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.9)" }} />
          </button>
        </div>

        {/* author strip */}
        {(authorName || authorEmail) && (
          <div
            className="flex items-center gap-3 mt-3 px-3 py-2 rounded-xl"
            style={{ background: "rgba(255,255,255,0.08)" }}
          >
            <Avatar
              sx={{
                width: 32, height: 32,
                bgcolor: "rgba(255,255,255,0.2)",
                fontSize: 12, fontWeight: 800,
                color: "white",
                border: "2px solid rgba(255,255,255,0.3)",
              }}
            >
              {initials}
            </Avatar>
            <div>
              {authorName && (
                <p className="text-white font-semibold text-sm leading-tight">{authorName}</p>
              )}
              {authorEmail && (
                <p className="text-white/60 text-xs leading-tight">{authorEmail}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── body ── */}
      <div className="px-6 pt-5 pb-4 bg-white">
        {children && <div className="mb-4">{children}</div>}

        <div className="relative">
          <textarea
            value={value}
            onChange={onChange}
            rows={4}
            placeholder="Share your thoughts…"
            className="w-full text-sm text-gray-700 placeholder-gray-400 resize-none focus:outline-none"
            style={{
              minHeight: 110,
              padding: "14px 16px",
              background: "#f8faff",
              border: "1.5px solid #e5eaf5",
              borderRadius: 12,
              fontFamily: "inherit",
              lineHeight: 1.6,
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#253978";
              e.target.style.boxShadow = "0 0 0 3px rgba(37,57,120,0.08)";
              e.target.style.background = "#ffffff";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e5eaf5";
              e.target.style.boxShadow = "none";
              e.target.style.background = "#f8faff";
            }}
          />
          {/* char count hint */}
          {value.length > 0 && (
            <span
              className="absolute bottom-3 right-3 text-xs text-gray-400 pointer-events-none"
            >
              {value.length}
            </span>
          )}
        </div>
      </div>

      {/* ── footer ── */}
      <div
        className="flex items-center justify-end gap-3 px-6 pb-5"
        style={{ background: "white" }}
      >
        <AppButton onClick={onClose} disabled={loading} variant="secondary">
          {cancelLabel}
        </AppButton>
        <AppButton
          onClick={onSubmit}
          disabled={loading || !value.trim()}
          variant="fill"
          style={{
            background: loading || !value.trim()
              ? "#9ca3af"
              : "var(--app-ai-assistant-gradient, linear-gradient(135deg, #253978, #1a2a5e))",
            boxShadow: loading || !value.trim()
              ? "none"
              : "0 4px 14px rgba(37,57,120,0.35)",
            borderRadius: "12px",
          }}
        >
          {loading ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {loadingLabel}
            </>
          ) : (
            <div className="flex items-center gap-2">
              {submitLabel}
              <SendIcon sx={{ fontSize: 14 }} />

            </div>
          )}
        </AppButton>
      </div>
    </Dialog>
  );
};

export default CommentTextareaModal;
